import type { AIGeneratedContent, QuestionnaireData } from '@data/publicPlans';

const SESSION_KEY = 'generatePlanReview';

export type GeneratePlanReviewSession = {
	questionnaire: QuestionnaireData;
	aiContent: AIGeneratedContent;
	contactEmail: string;
	databaseId?: number;
	frontendPlanId?: string;
	savedAt: string;
};

/** Avoid blowing sessionStorage quota with base64 photos. */
export function questionnaireForReviewStorage(q: QuestionnaireData): QuestionnaireData {
	const photoBeforeUrl =
		q.photoBeforeUrl && !q.photoBeforeUrl.startsWith('data:') ? q.photoBeforeUrl : undefined;
	return { ...q, photoBeforeUrl };
}

export function readGeneratePlanReview(): GeneratePlanReviewSession | null {
	try {
		const raw = sessionStorage.getItem(SESSION_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as GeneratePlanReviewSession;
		if (!parsed?.questionnaire || !parsed?.aiContent) return null;
		return parsed;
	} catch {
		return null;
	}
}

export function writeGeneratePlanReview(session: Omit<GeneratePlanReviewSession, 'savedAt'>): void {
	try {
		const payload: GeneratePlanReviewSession = {
			...session,
			questionnaire: questionnaireForReviewStorage(session.questionnaire),
			savedAt: new Date().toISOString()
		};
		sessionStorage.setItem(SESSION_KEY, JSON.stringify(payload));
	} catch (err) {
		console.warn('Could not persist generate-plan review session:', err);
	}
}

export function patchGeneratePlanReview(patch: Partial<Pick<GeneratePlanReviewSession, 'databaseId' | 'frontendPlanId' | 'aiContent' | 'questionnaire'>>): void {
	const existing = readGeneratePlanReview();
	if (!existing) return;
	writeGeneratePlanReview({
		questionnaire: patch.questionnaire ?? existing.questionnaire,
		aiContent: patch.aiContent ?? existing.aiContent,
		contactEmail: existing.contactEmail,
		databaseId: patch.databaseId ?? existing.databaseId,
		frontendPlanId: patch.frontendPlanId ?? existing.frontendPlanId
	});
}

export function clearGeneratePlanReview(): void {
	try {
		sessionStorage.removeItem(SESSION_KEY);
	} catch {
		/* ignore */
	}
}
